import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem("tk_admin_token", data.token);
      localStorage.setItem("tk_admin_user", JSON.stringify(data.user));
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : (detail || "Invalid credentials");
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center relative overflow-hidden px-6">
      <div className="grain-overlay" />
      {/* Ambient gold blob */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/10 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/5 blur-[120px] pointer-events-none" />

      <div className="relative w-full max-w-md" data-testid="admin-login-page">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-full border border-[#D4AF37]/40 flex items-center justify-center font-display font-bold text-[#D4AF37]">
              TK
            </div>
            <span className="font-display text-sm tracking-[0.25em] text-white/80 uppercase">
              Admin
            </span>
          </div>
          <h1 className="font-display font-bold uppercase text-3xl md:text-4xl leading-tight">
            Welcome <span className="text-gradient-gold">Back</span>
          </h1>
          <p className="text-[#B3B3B3] text-sm mt-3">Sign in to view contact submissions.</p>
        </div>

        <form onSubmit={onSubmit} className="glass rounded-2xl p-8" data-testid="admin-login-form">
          <div className="mb-5">
            <label className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-2 block">
              Email
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="admin@example.com"
                className="tk-input pl-11"
                required
                data-testid="admin-input-email"
              />
            </div>
          </div>
          <div className="mb-8">
            <label className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-2 block">
              Password
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={onChange}
                placeholder="••••••••"
                className="tk-input pl-11"
                required
                data-testid="admin-input-password"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="magnetic-btn solid w-full justify-center disabled:opacity-70"
            data-testid="admin-login-submit"
          >
            {loading ? (
              <>
                <span className="tk-spinner" />
                Signing in...
              </>
            ) : (
              <>
                Sign In <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-xs text-white/40">
          <button
            onClick={() => navigate("/")}
            className="hover:text-[#D4AF37] transition-colors"
            data-testid="admin-back-home"
          >
            ← Back to Portfolio
          </button>
        </p>
      </div>
    </div>
  );
}
